import { createAsyncThunk } from "@reduxjs/toolkit";
import {
  billingPreferencesApi,
  getConfigApi,
  notificationSettingsApi,
  quoteInvoiceSettingsApi,
  getStripeSetupLinkApi,
} from "helpers/ApiManager";
import { getFcmToken } from "helpers/firebase/fcm";
import { ApiResponse } from "types/ApiResponse";
import { ConfigPayload } from "types/config.api";
import {
  BillingPreferenceApiArgs,
  NotificationSettingsApiArgs,
  QuoteInvoiceSettingsApiArgs,
} from "types/setting.api";
import { ErrorHandler, SuccessToast } from "utils/toast";
import { FirebaseConfig, getFirebaseConfig } from "utils/getFirebaseConfig";

export const fetchFirebaseConfig = createAsyncThunk<
  FirebaseConfig | null,
  void,
  { rejectValue: string }
>("config/fetchFirebaseConfig", async (_, { rejectWithValue }) => {
  try {
    const data = await getFirebaseConfig();
    return data;
  } catch (err: any) {
    return rejectWithValue(err?.message || "Failed to fetch firebase config");
  }
});

export const getConfig = createAsyncThunk<
  ApiResponse<ConfigPayload>, // return type
  void,
  { rejectValue: string } // rejected type
>("config/getConfig", async (_, { rejectWithValue }) => {
  try {
    const response = await getConfigApi();
    return response;
  } catch (err: any) {
    console.log(err);
    return rejectWithValue(err?.message || "Failed to fetch");
  }
});

export const updateQuoteInvoiceSettings = createAsyncThunk<
  ApiResponse<ConfigPayload>, // return type
  QuoteInvoiceSettingsApiArgs, // argument type
  { rejectValue: string } // rejected type
>("config/updateQuoteInvoiceSettings", async (data, { rejectWithValue }) => {
  try {
    const response = await quoteInvoiceSettingsApi(data);
    SuccessToast(response.message);
    return response;
  } catch (err: any) {
    ErrorHandler(err);
    console.log(err);
    return rejectWithValue(err?.message || "Failed to update");
  }
});

export const billingPreferences = createAsyncThunk<
  ApiResponse<ConfigPayload>, // return type
  BillingPreferenceApiArgs, // argument type
  { rejectValue: string } // rejected type
>("config/billingPreferences", async (data, { rejectWithValue }) => {
  try {
    const response = await billingPreferencesApi(data);
    SuccessToast(response.message);
    return response;
  } catch (err: any) {
    ErrorHandler(err);
    console.log(err);
    return rejectWithValue(err?.message || "Failed to update");
  }
});

export const manageNotificationSettings = createAsyncThunk<
  ApiResponse<ConfigPayload>, // return type
  NotificationSettingsApiArgs, // argument type
  { rejectValue: string } // rejected type
>("config/manageNotificationSettings", async (param, { rejectWithValue }) => {
  try {
    const token = await getFcmToken();
    const response = await notificationSettingsApi({
      ...param,
      push_token: token,
    });
    SuccessToast(response.message);
    return response;
  } catch (err: any) {
    ErrorHandler(err);
    console.log(err);
    return rejectWithValue(err?.message || "Failed to manage notification");
  }
});

export const getStripeSetupLink = createAsyncThunk<
  ApiResponse<{ url: string }>,
  void,
  { rejectValue: string }
>("config/getStripeSetupLink", async (_, { rejectWithValue }) => {
  try {
    const response = await getStripeSetupLinkApi();
    return response;
  } catch (err: any) {
    return rejectWithValue(err?.message || "Failed to get Stripe setup link");
  }
});
